import { Collapse, Tab, Tabs } from "@blueprintjs/core";
import { generateSchema } from "decorator-form";
import React from "react";
import { default as Rjsf } from "react-jsonschema-form";
import draftToHtml from 'draftjs-to-html';
import DropdownSelect from "common/form/widgets/common/DropdownSelect";
import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import IOption from "common/models/IOption";
import { EntityEditWrapper } from "core/components/EntityEditWrapper";
import Label from "core/components/label/Label";
import AppToaster from "core/util/AppToaster";
import TokenSession from "core/util/session/TokenSession";
import ArticleList from "../article/admin/ArticleList";
import CategoryList from "./admin/CategoryList";
import { Category } from "./entities";
import { CategoryTypeRegistry } from "./ICategoryType";
import convertToHtml from "common/fragments/ConvertToHtml";

/**
 * loads and renders a Category with edit controls (if user is allowed to edit)
 *
 * TODO make this less messy:
 * 1. generic "create dialog" component?
 * 2. separate components for each of the tabs, maybe the whole edit section?
 *
 * @author chills
 */

interface ICategoryEditProps {
	categoryId?: string | number;
	externalId?: string;
	guid?: string;
}

interface ICategoryEditState {
	adding: null | "child" | "article";
}

const CategoryEditWrapper = EntityEditWrapper.ofType<Category>();

const formSchema = generateSchema(Category);

export class CategoryEdit extends React.Component<ICategoryEditProps, ICategoryEditState> {

	public state = { adding: null };

	public render() {

		const params = {};

		if (this.props.categoryId) {
			params['id'] = this.props.categoryId;
		} else if (this.props.guid) {
			params['guid'] = this.props.guid;
		} else if (this.props.externalId) {
			params['externalId'] = this.props.externalId;
		} else {
			// if no identifier supplied, don't try to load
			return (
				<NonIdealState
					title="No Category Selected"
					description=""
				/>
			);
		}

		return (
			<CategoryEditWrapper
				entity={Category}
				onAfterLoad={this.onAfterLoad}
				onBeforeSave={this.onBeforeSave}
				onAfterSave={this.onAfterSave}
				canEdit={TokenSession.hasRoles("ROLE_ADMIN")}
				params={params}
				title={<Label k="category.edit" v="Edit Category" />}
			>
				{(args) => {

					if (args.loading) {
						return <Loading />;
					}

					if (!args.data) {
						return null;
					}

					const category = (args.data as Category<{ type: string; data: any }>) || null;

					const categoryType = category.uiSchema && category.uiSchema.type && CategoryTypeRegistry.getObject(category.uiSchema.type) || null;

					return (
						<div>
							<Collapse isOpen={args.editing}>
								<div style={{padding: '15px', backgroundColor: '#f5f8fa'}}>
									<Tabs id="category:edit">
										<Tab
											id="general"
											title="General"
											panel={
												<Rjsf
													{...formSchema}
													formData={{...category}}
													onChange={(f) => args.onChange({...args.data, ...f.formData})}
												>
													<div />
												</Rjsf>
											}
										/>
										<Tab
											id="subcategories"
											title="Subcategories"
											panel={
												<CategoryList
													knowledgeBaseId={category.knowledgeBase.id}
													parentId={category.id}
												/>
											}
										/>
										<Tab
											id="articles"
											title="Articles"
											panel={
												<ArticleList
													categoryId={category.id}
													knowledgeBaseId={category.knowledgeBase.id}
												/>
											}
										/>
										<Tab
											id="appearance"
											title="Appearance"
											panel={
												<div>
													<DropdownSelect
														value={category.uiSchema.type}
														options={CategoryTypeRegistry.getAll().map<IOption<string>>((ct) => ({value: ct.type, label: ct.label, icon: ct.icon}))}
														onChange={(type) => args.onChange({...args.data, uiSchema: {...args.data.uiSchema, type}})}
													/>
													{
														categoryType &&
															typeof categoryType.renderAppearanceForm === 'function' &&
															categoryType.renderAppearanceForm(category.uiSchema.data, (data) => args.onChange({...args.data, uiSchema: {...args.data.uiSchema, data}}))
													}
												</div>
											}
										/>
									</Tabs>
								</div>
							</Collapse>
							{
								categoryType ? categoryType.render({category, editing: args.editing, onChange: args.onChange}) : null
							}
						</div>
					);
				}}
			</CategoryEditWrapper>
		);
	}

	private onAfterLoad = (category: Category) => {
		const o = {
			...category,
			uiSchema: !!category.uiSchema && JSON.parse(category.uiSchema) || {},
			tempIconUrl: category.iconUrl
		};

		if (o['description'] === null) {
			o['description'] = "";
		}

		return o;
	}

	private onBeforeSave = (data: Category) => {
		const updateParams = {
			id: data.id,
			externalId: data.externalId || null,
			name: data.name || "",
			description: convertToHtml(data.description) || null,
			uiSchema: JSON.stringify(data.uiSchema || "{}"),
			sortIndex: data.sortIndex || 0,
			published: data.published || false
		};

		if (data.tempIconUrl !== data.iconUrl) {
			updateParams['iconUrl'] = data.tempIconUrl;
		}

		return updateParams;
	}

	private onAfterSave = (res: any) => {
		AppToaster.show({
			message: 'Saved',
			intent: "success"
		});
	}
}



// WEBPACK FOOTER //
// ./src/panels/kb/category/CategoryEdit.tsx